import type { AppError } from '@repo/api-client';

/**
 * Map an `AppError` onto a user-readable title + body for display in
 * a toast or alert. UI never shows raw `kind` strings or stack
 * traces; this is the single chokepoint where translation happens.
 */
export interface DescribedError {
  title: string;
  message: string;
}

export function describeAppError(error: AppError | Error | unknown): DescribedError {
  const e =
    error && typeof error === 'object' && 'kind' in error
      ? (error as AppError)
      : null;
  if (!e) {
    const message = error instanceof Error ? error.message : 'Unexpected error';
    return { title: 'Something went wrong', message };
  }
  switch (e.kind) {
    case 'auth':
      return {
        title: 'Sign in to continue',
        message: e.message || 'Your session has expired or is invalid.',
      };
    case 'forbidden':
      return {
        title: 'Not allowed',
        message: e.message || "You don't have permission for that.",
      };
    case 'not-found':
      return {
        title: 'Not found',
        message: e.message || "We couldn't find what you were looking for.",
      };
    case 'conflict':
      return {
        title: 'Conflict',
        message: e.message || "That can't be done in the current state.",
      };
    case 'validation': {
      const fieldList = Object.values(e.details.fields ?? {}).flat();
      const message =
        fieldList.length > 0
          ? fieldList.join(' ')
          : e.message || 'Some details look off. Double-check the form.';
      return { title: 'Check the form', message };
    }
    case 'rate-limited':
      return {
        title: 'Too many requests',
        message: e.message || 'Slow down and try again in a moment.',
      };
    case 'network':
      return {
        title: 'No connection',
        message:
          e.message ||
          'Your device looks offline. Reconnect and try again.',
      };
    case 'timeout':
      return {
        title: 'Took too long',
        message: e.message || "The server didn't reply in time. Try again.",
      };
    case 'server':
      return {
        title: 'Server error',
        message:
          e.message ||
          "We're working on it. Please try again in a moment.",
      };
    default:
      return {
        title: 'Something went wrong',
        message: e.message || 'Unexpected error',
      };
  }
}
